import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './supplierEnquiriesModuleConfig';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SupplierEnquiryLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  qty?: number | null;
  requisitionQty?: number | null;
  itemNotes?: string | null;
  item?: {
    level1?: string | null;
    level2?: string | null;
    level3?: string | null;
  } | null;
  unit?: { id?: number; ucode?: string | null; descr?: string | null } | null;
}

export interface SupplierEnquiry {
  id: number;
  enqDate?: string | null;
  caseId: number;
  supplierId: number;
  ref?: string | null;
  requisitionId?: number | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  lines?: SupplierEnquiryLine[];
  _count?: { lines: number; quotations?: number };
  supplyCase?: {
    id: number;
    ref?: string | null;
    caseDate?: string | null;
    eta?: string | null;
    vessel?: { id: number; vesselName: string } | null;
    port?: { name?: string | null; code?: string | null } | null;
  } | null;
  requisition?: {
    id: number;
    ref?: string | null;
    reqDate?: string | null;
    caseId?: number | null;
    vessel?: { id: number; vesselName: string } | null;
  } | null;
  supplier?: {
    id: number;
    name: string;
    code?: string | null;
    email?: string | null;
    isActive?: boolean;
  } | null;
  rfqPublicLinkConfigured?: boolean;
  rfqPublicExpiresAt?: string | null;
  rfqPublicSubmittedAt?: string | null;
}

export interface SupplierEnquiryRfqLinkStatus {
  configured: boolean;
  expiresAt: string | null;
  submittedAt: string | null;
}

export const useSupplierEnquiries = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<SupplierEnquiry> | SupplierEnquiry[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<SupplierEnquiry>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupplierEnquiry = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupplierEnquiry>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export const useSupplierEnquiryRfqLinkStatus = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupplierEnquiryRfqLinkStatus>(
    id ? `/supplier-enquiries/${id}/rfq-link` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { status: data, isLoading, isError: error, mutate };
};
